import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormGroup, Validators, FormControl } from "@angular/forms";
import { NavigationService } from "../../../core/services/navigation.service";
import { AuthenticationService } from "../../../core/services/authentication.service";
import { catchError, filter, first, map, takeUntil } from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ConfigEntityService } from "../../../core/services/config-entity.service";
import { lastValueFrom, of, Subject, Subscription } from "rxjs";
import { EnvironmentService } from "../../../core/services/environment.service";
import { User } from "../../../core/models/user";
import OktaAuth from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import {
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from "@azure/msal-angular";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from "@azure/msal-browser";
import { AuthType } from "../../../core/models/globals";
import { HeaderService } from "../../../core/services/header.service";
import { SyncConfigService } from "../../../core/services/sync-config.service";
import { LogInUiConfigService } from "../../../core/services/logInUiConfig.service";
import { DomSanitizer } from "@angular/platform-browser";
import { UserService } from "../../../core/services/user.service";
import { UserRoleNames } from "../../../core/models/users";
import { AuthService } from "@auth0/auth0-angular";
import { MatDialog } from "@angular/material/dialog";
import { AboutUsComponent } from "../../../core/header/dialogs/about-us/about-us.component";
import { TermsComponent } from "../../../core/header/dialogs/terms/terms.component";
import { PrivacyPolicyComponent } from "../../../core/header/dialogs/privacy-policy/privacy-policy.component";
import { ConcurrentUsersService } from "src/app/core/services/concurrent-users.service";
import { OAuthService } from "angular-oauth2-oidc";
import { DeploymentService } from "src/app/core/services/deployment.service";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  returnUrl: string;
  fieldTextType: boolean = true;
  isLoggingIn: boolean = false;
  errorMsg: string = "";
  toShowLabel = this.env.envVariables.companyName;
  authType = this.authenticationService.getAuthenticationType();
  private readonly _destroying$ = new Subject<void>();
  newsFlash: string = null;
  showNewsFlash: boolean = false;
  newsFlashSub: Subscription;
  title = this.loginUiConfig.logInUiConfigVariables.title;
  logoAndTitle = this.loginUiConfig.logInUiConfigVariables.logoAndTitle;
  configureLogInPage =
    this.loginUiConfig.logInUiConfigVariables.configureLogInPage;

  isClientLogin = this.router.url.includes("desktopapp"); // check for desktopapp
  isDesktopClientLogin = this.router.url.includes("desktopapp");
  vdiModeOnly = this.env.envVariables.vdiModeOnly;

  // New properties for admin login
  showAdminLogin: boolean = false;
  isDefaultAuthType: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: NavigationService,
    private authenticationService: AuthenticationService,
    private configEntityService: ConfigEntityService,
    private snackbar: MatSnackBar,
    private env: EnvironmentService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private headerService: HeaderService,
    private syncConfig: SyncConfigService,
    private loginUiConfig: LogInUiConfigService,
    public sanitizer: DomSanitizer,
    public userService: UserService,
    public openIdAuth: AuthService,
    public dialog: MatDialog,
    private _concurrentService: ConcurrentUsersService,
    private oauthService: OAuthService,
    private deploymentService: DeploymentService
  ) { }

  async ngOnInit() {
    this.isClientLogin = this.router.url.includes("desktopapp");
    this.sharedService.subNavData("Login");

    // Check if current auth type is default (0 or 1)
    this.isDefaultAuthType = [0, 1].includes(this.authType);

    this.initializeLoginForm();

    let userObj: User = {
      id: 1,
      username: "",
      password: "",
      firstName: "",
      lastName: "",
      role: "",
      token: "",
      isUserVdiOnly: false,
    };

    // Handle SSO authentication flows only if not showing admin login
    if (!this.showAdminLogin && !this.isDefaultAuthType) {
      await this.handleSSOAuthentication(userObj);
    } else if (this.isDefaultAuthType) {
      // Handle default authentication flow
      this.handleDefaultAuthentication();
    }

    this.headerService.getSetTimeZone();
  }

  private initializeLoginForm() {
    this.authenticationService.isDefaultAdminLogin = true;
    this.loginForm = new FormGroup({
      loginUsername: new FormControl(
        this.route.snapshot.queryParams["user"] || "",
        [Validators.required, Validators.pattern(/^[A-Za-z0-9@_.-]*$/)]
      ),
      loginPassword: new FormControl("", Validators.required),
    });
  }

  private async handleSSOAuthentication(userObj: User) {
    if (this.authType == AuthType.Okta) {
      let isAuthenticated = await this.oktaAuth.isAuthenticated();
      if (isAuthenticated) {
        await this.oktaAuth.getUser().then((data) => {
          userObj.firstName = data.given_name;
          userObj.lastName = data.family_name;
          userObj.username = data.preferred_username;

          this.userService.getUserRole(data.preferred_username).subscribe({
            next: (data) => {
              let roleNum = Number(data);
              userObj.role = this.mapRoleNumber(roleNum);
              userObj.token = this.oktaAuth.getAccessToken();
              this.handleAfterLogin(userObj);
            },
            error: () => { },
          });
        });
      }
    } else if (this.authType == AuthType.PingIdentity) {
      this.authenticationService.logInPageRedirect = true;
      this.router.navigate(['Home']);
    } else if (this.authType == AuthType.AzureAD) {
      this.authenticationService.logInPageRedirect = true;
      this.router.navigate(['Home']);
    } else if (this.authType == AuthType.OpenId) {
      this.openIdAuth.isAuthenticated$.subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.openIdAuth.user$.subscribe((user) => {
            userObj = {
              ...userObj,
              firstName: user.given_name,
              lastName: user.family_name,
              username: user.name,
            };

            // Get user role from your API or service
            this.userService.getUserRole(user.name).subscribe({
              next: async (data) => {
                const roleNum = Number(data);
                userObj.role = this.mapRoleNumber(roleNum);
                userObj.token = await lastValueFrom(
                  this.openIdAuth.getAccessTokenSilently()
                );
                this.handleAfterLogin(userObj);
              },
              error: () => {
                // Handle error
              },
            });
          });
        }
      });
    }
  }

  private handleDefaultAuthentication() {
    // get return url from route parameters or default to '/'
    
    this.returnUrl =
      this.route.snapshot.queryParams["returnUrl"] || "/Job List";
    this.authenticationService.currentUser.subscribe((user) => {
      if (user != null) {
        this.checkLicenseExpiry();
        this.navigateAfterLogin(user);
        this.deploymentService.initializeHeadnodeData();
      }
    });
  }

  private mapRoleNumber(roleNum: number): string {
    switch (roleNum) {
      case 0:
        return UserRoleNames.GlobalAdmin;
      case 1:
        return UserRoleNames.DeploymentAdmin;
      case 2:
        return UserRoleNames.PowerUser;
      case 3:
        return UserRoleNames.JobOperator;
      case 4:
        return UserRoleNames.Reader;
      default:
        return "";
    }
  }

  private navigateAfterLogin(user: User) {
    if (this.isClientLogin) {
      this.router.navigate(["/Client Sessions/desktopapp/sessions"]);
    } else if (this.vdiModeOnly && user.isUserVdiOnly && !this.isClientLogin) {
      this.router.navigate(["/Client Sessions/webapp/sessions"]);
    } else {
      if (user?.role == UserRoleNames.Reader) {
        this.router.navigate(["/Analytics"]);
      } else if (user?.role == UserRoleNames.GlobalAdmin) {
        this.router.navigate(["/Home"]);
      } else {
        this.router.navigate(["/Job List"]);
      }
    }
  }

  // New method to toggle admin login
  toggleAdminLogin() {
    this.showAdminLogin = !this.showAdminLogin;
    if (this.showAdminLogin) {
      this.initializeLoginForm();
    }
    this.errorMsg = ""; // Clear any existing error messages
  }

  // New method to determine if login form should be shown
  shouldShowLoginForm(): boolean {
    return this.isDefaultAuthType || this.showAdminLogin;
  }

  // New method to determine if SSO button should be shown
  shouldShowSSOButton(): boolean {
    return !this.isDefaultAuthType && !this.showAdminLogin;
  }

  async logIn() {
    // await this.msalService.instance.loginRedirect();
  }

  get authenticated(): boolean {
    return this.msalService.instance.getActiveAccount() ? true : false;
  }

  async handleAfterLogin(userObj) {
    // check if user is vdi user or not make a call to the backend
    localStorage.setItem("currentUser", JSON.stringify(userObj));

    this.authenticationService.updateCurrentUserValue(userObj);
    const licenseExpired = await lastValueFrom(this.checkLicenseExpiry());
    const userSessionAdded = await lastValueFrom(this._concurrentService.add());
    const isUserVdiOnly = await lastValueFrom(
      this.checkIfUserIsVdiOnly(userObj.username)
    );
    this.deploymentService.initializeHeadnodeData();
    localStorage.setItem(
      "currentUser",
      JSON.stringify({
        ...userObj,
        isUserVdiOnly: isUserVdiOnly,
      })
    );

    if (userSessionAdded != true) {
      this.snackbar.open(userSessionAdded as string, "Ok", {
        duration: 5000,
      });
      setTimeout(() => {
        this.authenticationService.logout();
      }, 1000);
    } else {
      if (!licenseExpired) {
        this.navigateAfterLogin({ ...userObj, isUserVdiOnly });
      } else {
        this.router.navigate(["/Configurator"]);
      }
    }
  }

  checkIfUserIsVdiOnly(userName) {
    return this.authenticationService.isUserVdiOnly(userName).pipe(
      map((data: any) => {
        let isVdiUserEnabled = data;
        return !isVdiUserEnabled;
      }),
      catchError((err) => {
        console.error(err);
        return of(true);
      })
    );
  }

  checkLicenseExpiry() {
    return this.configEntityService.checkIfLoginEnabled().pipe(
      map((data: any) => {
        let isLoginEnabled = data;
        if (isLoginEnabled === false) {
          localStorage.setItem("LicenseExpired", "true");
        }
        return !isLoginEnabled;
      }),
      catchError((err) => {
        console.error(err);
        return of(true);
      })
    );
  }

  onSubmit() {
    console.log("SSSS");
    // If admin login is enabled or it's default auth type, use default authentication
    if (this.showAdminLogin || this.isDefaultAuthType) {
      this.handleDefaultLogin();
    } else {
      // Handle SSO login based on auth type
      this.handleSSOLogin();
    }
  }

  private handleDefaultLogin() {
    let isLoginEnabled: boolean = false;
    this.isLoggingIn = true;
    if (this.loginForm.hasError("invalidError")) {
      this.loginForm.setErrors(null);
    }
    this.configEntityService.checkIfLoginEnabled().subscribe({
      next: (data) => {
        isLoginEnabled = <any>data;
        isLoginEnabled === false
          ? localStorage.setItem("LicenseExpired", "true")
          : localStorage.removeItem("LicenseExpired");
        if (this.authenticationService.currentUserValue) {
          this.authenticationService.logout();
        } else {
          if (!this.loginForm.valid) {
            this.loginForm.markAllAsTouched();
            this.isLoggingIn = false;
          } else {
            this.isLoggingIn = true;
            this.authenticationService
              .login(
                this.loginForm.value.loginUsername,
                this.loginForm.value.loginPassword,
                this.showAdminLogin
              )
              .pipe(first())
              .subscribe({
                next: (data: User) => {
                  if (isLoginEnabled == false) {
                    this.router.navigate(["/Configurator"]);
                  } else {
                    this.navigateAfterLogin(data);
                  }
                  this.isLoggingIn = false;
                },
                error: (error) => {
                  let { message } = JSON.parse(JSON.stringify(error.error));
                  this.errorMsg = message;
                  this.loginForm.setErrors({ invalidError: true });
                  this.isLoggingIn = false;
                },
              });
          }
        }
      },
      error: () => {
        this.snackbar.open("Error while logging in", "Ok", {
          duration: 7000,
        });
        this.isLoggingIn = false;
      },
    });
  }

  private handleSSOLogin() {
    if (this.authType == AuthType.Okta) {
      this.oktaLogin();
    } else if (this.authType == AuthType.AzureAD) {
      this.azureAdLogin();
    } else if (this.authType == AuthType.OpenId) {
      this.openIdLogin();
    } else if (this.authType == AuthType.SamlId) {
      this.samlLogin();
    } else if (this.authType == AuthType.PingIdentity) {
      this.oauthService.initLoginFlow();
    }
  }

  async oktaLogin() {
    try {
      await this.oktaAuth.signInWithRedirect({ originalUri: "/Login" });
    } catch (err) {
      console.error(err);
    }
  }

  azureAdLogin() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  async openIdLogin() {
    await this.openIdAuth.loginWithRedirect();
  }

  togglePassswordIcon() {
    this.fieldTextType = !this.fieldTextType;
  }

  ngOnDestroy() {
    this.newsFlashSub?.unsubscribe();
  }

  samlLogin() {
    this.authenticationService.samlLogin().subscribe({
      next: (data: { status; uri }) => {
        window.location.href = data.uri;
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  helpMenuClicked(id: string) {
    let currentButton = event.currentTarget;
    if (id == "About") {
      const dialogRef = this.dialog.open(AboutUsComponent, {
        width: "40%",
        height: "50%",
        autoFocus: true,
        disableClose: true,
      });
    } else if (id == "Terms") {
      const dialogRef = this.dialog.open(TermsComponent, {
        width: "70vw",
        height: "90vh",
      });
    } else if (id == "Privacy") {
      const dialogRef = this.dialog.open(PrivacyPolicyComponent, {
        width: "70vw",
        height: "90vh",
      });
    } else if (id == "Support") {
      this.gotoExternalDomain(this.env.envVariables.supportLink);
    } else {
      this.gotoExternalDomain(this.env.envVariables.helpLink);
    }
  }

  gotoExternalDomain(url: string) {
    (window as any).open(url, "_blank");
  }
}
